// Performance Appraisals for the HR module of Khyati Portal / RSR International.
// KPI is sourced from the Performance module (performance_monthly) and blended
// with an attendance-derived score to produce a final rating + letter grade.
// Employees without portal access / KPI data fall back to attendance only.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import {
  HRC, Card, SectionTitle, Th, Td, Table,
  Pill, Btn, Stat, Field, input, MonthPicker, Modal, Empty, Loading,
} from './hrkit';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from 'recharts';

const num = (v) => Number(v || 0);
const fix1 = (v) => (v == null || v === '' ? null : Number(v).toFixed(1));

// Consistent grade → colour mapping used everywhere.
const GRADE_COLOR = (g) => {
  switch (String(g || '').toUpperCase()) {
    case 'A+':
    case 'A': return HRC.green;
    case 'B': return HRC.navy;
    case 'C': return HRC.amber;
    case 'D': return HRC.red;
    default: return HRC.muted;
  }
};

const TYPE_LABEL = (t) =>
  String(t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || '—';

// Coloured grade pill (overrides the generic Pill colour map).
const GradePill = ({ grade }) => {
  const c = GRADE_COLOR(grade);
  if (!grade) return <span style={{ color: HRC.muted }}>—</span>;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 12,
      fontWeight: 800, color: c, background: c + '1A', whiteSpace: 'nowrap',
    }}>{grade}</span>
  );
};

// Tiny 0–100 bar for the attendance score.
const ScoreBar = ({ value }) => {
  const v = Math.max(0, Math.min(100, num(value)));
  const c = v >= 85 ? HRC.green : v >= 65 ? HRC.amber : HRC.red;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
      <div style={{ flex: 1, height: 6, background: '#EEE', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${v}%`, height: '100%', background: c, borderRadius: 4 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color: HRC.text, width: 30, textAlign: 'right' }}>
        {Math.round(v)}
      </span>
    </div>
  );
};

export default function Appraisals(props) {
  const { month, setMonth } = props || {};
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [active, setActive] = useState(null); // row being reviewed

  const load = useCallback(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    api.get('/hr/appraisals', { params: { month } })
      .then((r) => { if (alive) setRows(r.data?.data || []); })
      .catch((e) => {
        if (!alive) return;
        setErr(e.response?.data?.message || 'Could not load appraisals');
        setRows([]);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [month]);

  useEffect(load, [load]);

  // ── Summary metrics ──
  const rated = rows.filter((r) => r.final_rating != null);
  const avgFinal = rated.length
    ? rated.reduce((s, r) => s + num(r.final_rating), 0) / rated.length
    : 0;
  const topGraded = rows.filter((r) => ['A+', 'A'].includes(String(r.grade || '').toUpperCase())).length;
  const withKpi = rows.filter((r) => r.has_kpi).length;
  const attendanceOnly = rows.length - withKpi;
  const published = rows.filter((r) => r.appraisal_status === 'published').length;

  // ── Chart: top 12 by final rating ──
  const chartData = [...rated]
    .sort((a, b) => num(b.final_rating) - num(a.final_rating))
    .slice(0, 12)
    .map((r) => ({
      name: (r.full_name || r.employee_code || '').split(' ')[0],
      rating: Number(num(r.final_rating).toFixed(1)),
      grade: r.grade,
    }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Toolbar */}
      <Card>
        <SectionTitle right={<MonthPicker value={month} onChange={setMonth} />}>
          Performance Appraisals
        </SectionTitle>
        <div style={{ fontSize: 12, color: HRC.muted, marginTop: -4 }}>
          KPI is sourced from the <strong style={{ color: HRC.navy }}>Performance</strong> module
          and blended with attendance (0.7 × KPI + 0.3 × attendance; attendance-only when no KPI).
        </div>
      </Card>

      {err && (
        <div style={{ color: HRC.red, fontSize: 13, background: HRC.red + '12', padding: '10px 14px', borderRadius: 8 }}>
          {err}
        </div>
      )}

      {/* Summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 14 }}>
        <Stat label="Avg Final Rating" value={avgFinal.toFixed(1)} sub={`${rated.length} rated`} accent={HRC.navy} />
        <Stat label="Graded A+ / A" value={topGraded} sub="top performers" accent={HRC.green} />
        <Stat label="With KPI Data" value={withKpi} sub="from Performance" accent={HRC.violet === '#EEEDF8' ? HRC.navy : HRC.navy} />
        <Stat label="Attendance-only" value={attendanceOnly} sub="no portal KPI" accent={HRC.amber} />
        <Stat label="Published" value={published} sub={`${rows.length} total`} accent={published ? HRC.green : HRC.muted} />
      </div>

      {/* Chart */}
      {!loading && chartData.length > 0 && (
        <Card>
          <SectionTitle>Top Final Ratings</SectionTitle>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 6, right: 10, left: -16, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: HRC.muted }} interval={0} angle={-20} textAnchor="end" height={50} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: HRC.muted }} />
              <Tooltip
                cursor={{ fill: HRC.violet }}
                formatter={(v, _n, p) => [`${v}  (${p?.payload?.grade || '—'})`, 'Final']}
              />
              <Bar dataKey="rating" radius={[4, 4, 0, 0]}>
                {chartData.map((d, i) => <Cell key={i} fill={GRADE_COLOR(d.grade)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Main table */}
      <Card>
        <SectionTitle>
          Appraisal Sheet — {month}
        </SectionTitle>
        <div style={{ fontSize: 12, color: HRC.muted, fontWeight: 600, marginBottom: 8 }}>
          {rows.length} employee{rows.length === 1 ? '' : 's'}
        </div>

        {loading ? <Loading /> : rows.length === 0 ? (
          <Empty>No appraisal data for {month}. KPI demo data exists for 2026-05 and 2026-06.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Dept</Th>
                <Th>Type</Th>
                <Th>KPI Score</Th>
                <Th>Attendance</Th>
                <Th>Final</Th>
                <Th>Grade</Th>
                <Th>Status</Th>
                <Th style={{ textAlign: 'right' }}>Action</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.employee_id}>
                  <Td style={{ fontWeight: 600 }}>{r.employee_code || '—'}</Td>
                  <Td>
                    <div style={{ fontWeight: 600 }}>{r.full_name || '—'}</div>
                    <div style={{ fontSize: 11, color: HRC.muted }}>{r.designation || ''}</div>
                  </Td>
                  <Td style={{ color: HRC.muted }}>{r.department_name || '—'}</Td>
                  <Td style={{ color: HRC.muted, fontSize: 12 }}>{TYPE_LABEL(r.employee_type)}</Td>

                  <Td>
                    {r.has_kpi ? (
                      <span style={{ fontWeight: 700, color: HRC.text }}>{fix1(r.kpi_score) ?? '—'}</span>
                    ) : (
                      <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ color: HRC.muted }}>—</span>
                        <span style={{
                          fontSize: 9, fontWeight: 700, color: HRC.amber, background: HRC.amber + '1A',
                          padding: '1px 6px', borderRadius: 10, whiteSpace: 'nowrap',
                        }}>no KPI · attendance-only</span>
                      </span>
                    )}
                  </Td>

                  <Td><ScoreBar value={r.attendance_score} /></Td>

                  <Td style={{ fontWeight: 800, color: GRADE_COLOR(r.grade) }}>
                    {fix1(r.final_rating) ?? '—'}
                  </Td>

                  <Td><GradePill grade={r.grade} /></Td>

                  <Td>
                    {r.appraisal_status === 'published'
                      ? <Pill value="published" />
                      : r.appraisal_status === 'draft'
                        ? <Pill value="draft" />
                        : <span style={{ color: HRC.muted }}>—</span>}
                  </Td>

                  <Td style={{ textAlign: 'right' }}>
                    <Btn size="sm" variant="soft" onClick={() => setActive(r)}>Review</Btn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {active && (
        <ReviewModal
          row={active}
          month={month}
          onClose={() => setActive(null)}
          onSaved={() => { setActive(null); load(); }}
        />
      )}
    </div>
  );
}

// ───────────────────────────── Review modal ─────────────────────────────
function ReviewModal({ row, month, onClose, onSaved }) {
  const [comments, setComments] = useState(row.comments || '');
  const [override, setOverride] = useState(
    row.final_rating != null ? String(Number(row.final_rating).toFixed(1)) : ''
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const finalRating = override === '' ? num(row.final_rating) : num(override);
  const overridden = override !== '' &&
    Number(override) !== Number(num(row.final_rating).toFixed(1));

  // Re-derive a grade for the (possibly overridden) final rating so the modal
  // preview stays consistent with the rest of the dashboard.
  const previewGrade = (v) => {
    if (v >= 90) return 'A+';
    if (v >= 80) return 'A';
    if (v >= 65) return 'B';
    if (v >= 50) return 'C';
    return 'D';
  };
  const grade = overridden ? previewGrade(finalRating) : (row.grade || previewGrade(finalRating));

  const publish = () => {
    setSaving(true);
    setErr('');
    api.post('/hr/appraisals', {
      employee_id: row.employee_id,
      period_month: month,
      kpi_score: row.has_kpi ? num(row.kpi_score) : null,
      attendance_score: num(row.attendance_score),
      final_rating: Number(finalRating.toFixed(1)),
      comments: comments || '',
      status: 'published',
    })
      .then(() => onSaved())
      .catch((e) => setErr(e.response?.data?.message || 'Could not publish appraisal'))
      .finally(() => setSaving(false));
  };

  const Metric = ({ label, value, color }) => (
    <div style={{ flex: 1, minWidth: 90 }}>
      <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: color || HRC.text, marginTop: 2 }}>{value}</div>
    </div>
  );

  const hasActivities = row.total_activities != null && num(row.total_activities) > 0;

  return (
    <Modal title="Appraisal Review" width={560} onClose={onClose}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: HRC.text }}>{row.full_name || '—'}</div>
          <div style={{ fontSize: 12, color: HRC.muted }}>
            {row.employee_code} · {row.designation || '—'} · {row.department_name || '—'}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>Period</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: HRC.navy }}>{month}</div>
        </div>
      </div>

      {/* Breakdown */}
      <div style={{
        display: 'flex', gap: 12, flexWrap: 'wrap', background: HRC.pageBg,
        border: `1px solid ${HRC.border}`, borderRadius: 10, padding: 14, marginBottom: 14,
      }}>
        <Metric
          label="KPI Score"
          value={row.has_kpi ? (fix1(row.kpi_score) ?? '—') : '—'}
          color={row.has_kpi ? HRC.navy : HRC.muted}
        />
        <Metric label="Attendance" value={Math.round(num(row.attendance_score))} color={HRC.amber} />
        <Metric label="Final Rating" value={finalRating.toFixed(1)} color={GRADE_COLOR(grade)} />
        <div style={{ minWidth: 70 }}>
          <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Grade</div>
          <div style={{ marginTop: 4 }}><GradePill grade={grade} /></div>
        </div>
      </div>

      {!row.has_kpi && (
        <div style={{
          fontSize: 12, color: HRC.amber, background: HRC.amber + '12',
          padding: '8px 12px', borderRadius: 8, marginBottom: 14,
        }}>
          No KPI on record for this employee — final rating is derived from attendance only.
        </div>
      )}

      {/* Activity breakdown (when present) */}
      {hasActivities && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
            Activities ({num(row.total_activities)})
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <ActChip label="On time" value={num(row.on_time)} color={HRC.green} />
            <ActChip label="Late" value={num(row.late)} color={HRC.amber} />
            <ActChip label="Not done" value={num(row.not_done)} color={HRC.red} />
          </div>
        </div>
      )}

      {/* Comments */}
      <Field label="Reviewer Comments">
        <textarea value={comments} onChange={(e) => setComments(e.target.value)} rows={3}
          placeholder="Strengths, areas to improve, notes for this period…"
          style={{ ...input, resize: 'vertical' }} />
      </Field>

      {/* Optional override */}
      <div style={{ marginTop: 14 }}>
        <Field label="Override Final Rating (optional, 0–100)">
          <input
            type="number" min={0} max={100} step="0.1"
            value={override}
            onChange={(e) => setOverride(e.target.value)}
            placeholder={`Computed: ${num(row.final_rating).toFixed(1)}`}
            style={input}
          />
        </Field>
        {overridden && (
          <div style={{ fontSize: 11, color: HRC.navy, marginTop: 4 }}>
            Overriding computed {num(row.final_rating).toFixed(1)} → {finalRating.toFixed(1)} (grade {grade})
          </div>
        )}
      </div>

      {err && <div style={{ color: HRC.red, fontSize: 13, marginTop: 12 }}>{err}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 18 }}>
        <div style={{ fontSize: 11, color: HRC.muted }}>
          {row.appraisal_status === 'published'
            ? 'This appraisal is already published — re-publishing updates it.'
            : 'Not yet published.'}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="primary" disabled={saving} style={{ background: HRC.green }} onClick={publish}>
            {saving ? 'Publishing…' : 'Publish Appraisal'}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

const ActChip = ({ label, value, color }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
    background: color + '12', borderRadius: 8, border: `1px solid ${color}33`,
  }}>
    <span style={{ fontSize: 16, fontWeight: 800, color }}>{value}</span>
    <span style={{ fontSize: 11, color: HRC.muted, fontWeight: 600 }}>{label}</span>
  </div>
);
